import { IWizView } from '../../../interfaces/iwiz-view';
import { BehaviorSubject } from 'rxjs';
import { NavigationExtras, Router, Routes } from '@angular/router';
import { WizRoot } from '../../../components/views/wiz-root';
import { IWizRoute } from '../../../interfaces/iwiz-route';
import { WIZ_EVENT_TYPE } from '@wizeoo/akm-core-lib';
import { WizEventManager } from '../../../events/wiz-event.manager';
import { WizEvent } from '../../../events/wiz-event';

// gestionnaire de navigation
// Statique
export class WizNavigationManager {

  // les vue en cours
  // listener sur currentView
  public static currentViews: BehaviorSubject<Map<string, IWizView>> = new BehaviorSubject(new Map());

  constructor() {
    // console.log ('wizNavigation builded');
  }

  // accesseur sur routeur, alimenté par WizModule
  public static router: Router = null;

  /** Demande de navigation avec passage de state local */
  public static navigateWithState<T>(pTo: string | any[], state: T): boolean {
    return this.navigate(pTo, { state });
  }

  // Demande de navigation
  // @param pTo : code de la destination
  // @Return : true si la navigation est autorisée
  public static navigate( pTo: string | any[], extras?: NavigationExtras): boolean {

    let to: string = null;
    let params: any = null;
    let realTargetTo: string = null;

    if (Array.isArray(pTo)) {
      to = pTo[0];
      realTargetTo = to;

      if (pTo.length > 0) {
        params = pTo[1];

        if (!!params) {
          realTargetTo = `${to}/:${Object.keys(params).join('/:')}`
        }
      }
    } else {
      to = pTo;
      realTargetTo = to;
    }

    // start navigation
    WizEventManager.dispatchEvent(WizRoot.root(),
      new WizEvent(WIZ_EVENT_TYPE.NAVIGATE_START, null, to));

    // on récupérer le chemin complet pour naviguer (pTo n'est potentiellement que le dernier élément
    // en cas de navigation sur n levels)
    const realTarget: IWizRoute = this.getPathRoute(to);
    const nav: any[] = [];
    if (realTarget) {
      nav.push(realTarget.fullPath);
    } else { // erreur, va planter, chemin inconnu...
      nav.push(to);
    }

    if (!!params) {
      nav.push(params);
    }

    WizNavigationManager.router.navigate(nav, extras).then(navigationValid => {
      if (navigationValid) {
        // start navigation
        WizEventManager.dispatchEvent(WizRoot.root(), new WizEvent(WIZ_EVENT_TYPE.NAVIGATE_END, null, to));
      }
    });

    return true; // a priori, sert à rien
  }

  // sortie de vue, désallocation
  public static unregisterView(view: IWizView) {
    WizNavigationManager.currentViews.getValue().delete(view.uuid);
    WizNavigationManager.currentViews.next(WizNavigationManager.currentViews.getValue());
    WizEventManager.dispatchEvent(WizRoot.root(),
    new WizEvent(WIZ_EVENT_TYPE.NAVIGATE_UNREGISTER, null, view));
  }

  // arrivée sur une nouvelle vue, alimentation de currentView
  public static registerView(view: IWizView) {
    WizNavigationManager.currentViews.next(WizNavigationManager.currentViews.getValue().set(view.uuid, view));
    WizEventManager.dispatchEvent(WizRoot.root(),
    new WizEvent(WIZ_EVENT_TYPE.NAVIGATE_REGISTER, null, view));
  }

  /**
   * donne le niveau de profondeur dans la pile de navigation (1 = racine)
   * @param view : la vue cible
   */
  public static getNavigationLevel(view: IWizView): number {
    let result: number = 1;

    result = this.getRecurseNavigationLevel(view,
      this.router.config, result);

    return result;
  }

  /**
   * donne le niveau de profondeur dans la pile de navigation (1 = racine)
   * @param view : la vue cible
   * @param IWizRoute[] : la collection de routes
   */
  private static getRecurseNavigationLevel(view: IWizView, routes: IWizRoute[], result: number): number {
    for (const route of routes) {
      if (route.path === view.code) { // porte directement le code
        // console.log('getNavigationLevel ' + route.path + ' ' + route.level);
        result = route.level;
      } else if (route.children) { // on test les enfants
        result = this.getRecurseNavigationLevel(view, route.children, result);
      }
    }
    return result;
  }

  /**
   * retourne le IWizRoute correspondant à un composant IWizView (depuis son type de classe)
   * @param view : la vue cible
   */
  public static getViewRoute(view: IWizView): IWizRoute {
    let result: IWizRoute = null;

    result = this.getRecurseViewRoute(view, this.router.config, result);

    return result;
  }

  /**
   * donne le IWizRoute correspondant au type d'instance de vue (vues statiques uniquement)
   * @param view : la vue cible
   * @param IWizRoute[] : la collection de routes
   */
  private static getRecurseViewRoute(view: IWizView, routes: IWizRoute[], result: IWizRoute): IWizRoute {
    for (const route of routes) {
      if (route.component && view instanceof route.component) { // porte directement le code
        // console.log('yo ' + route.path + ' ' + route.fullPath);
        result = route;
      } else if (route.children) { // on test les enfants
        result = this.getRecurseViewRoute(view, route.children, result);
      }
      if (result) {
        break;
      }
    }
    return result;
  }

  /**
   * Retourne le state local de la route courante.
   *
   * @returns state : State propre à la vue actuelle
   */
  public static getViewRouteState<T>(): T {
    // Angular garde les states de navigation dans l'objet window.history après navigation
    return (
      this.router.getCurrentNavigation()?.extras?.state || window.history.state
    );
  }

  /**
   * retourne le IWizRoute pour un path donné (pour collecte du chemin complet)
   * @param string : le path cible, pas le fullpath...
   */
  public static getPathRoute(path: string): IWizRoute {
    let result: IWizRoute = null;

    result = this.getRecursePathRoute(path,
      this.router.config);

    return result;
  }

  /**
   * donne le IWizRoute correspondant au path exact (pas le fullPath)
   * @param path : le path de la vue cible
   * @param IWizRoute[] : la collection de routes
   */
  private static getRecursePathRoute(path: string, routes: IWizRoute[]): IWizRoute {

    let result: IWizRoute = null;
    for (const route of routes) {
      if (route.path === path) { // porte directement le code
        // console.log('yo ' + route.path + ' ' + route.fullPath);
        result = route;
      } else if (route.children) { // on test les enfants
        result = this.getRecursePathRoute(path, route.children);
      }
      if (result) {
        break;
      }
    }
    return result;
  }

  /**
   * retourne le IWizRoute PARENT pour un path donné
   * @param string : le path cible, pas le fullpath...
   * @param IWizRoute[] : la collection de routes
   */
  public static getPathParentRoute(path: string, routes: IWizRoute[]): IWizRoute {
    let result: IWizRoute = null;
    result = this.getRecursePathParentRoute(path, routes);
    return result;
  }

  /**
   * donne le IWizRoute correspondant au parent du path fourni
   * @param path : le path de la vue cible
   * @param IWizRoute[] : la collection de routes
   */
  private static getRecursePathParentRoute(path: string, routes: IWizRoute[]): IWizRoute {

    let result: IWizRoute = null; // le parent
    routes.forEach((route: IWizRoute) => {
      if (route.children) {
        route.children.forEach((child: IWizRoute) => {
          if (child.path === path) { // trouvé l'enfant
            result = route; // on prend le parent
            return result;
          } else if (child.children) { // on test les enfants
            result = this.getRecursePathRoute(path, route.children);
          }
        });
      }
    });
    return result;
  }


  /**
   * ajout dynamique d'un route (nouvel onglet, par exemple)
   * @param parentPath : le path du parent, '' pour la racine
   * @param child : une IwizRoute correctement construite (code, label, compo..)
   *  const neoR: IWizRoute = {path: 'sub3', label: 'sub3title', component: HomeSub3View };
   *  WizNavigationManager.addDynamicRoute('home', neoR);
   *  WizNavigationManager.navigate('sub3');
   * TODO ajouter les contrôles de cohérence
   */
  public static addDynamicRoute(parentPath: string, child: IWizRoute): void {

    // on vérifie que le chemin demandé n'existe pas
    const alreadyThere: IWizRoute = this.getPathRoute(child.path);
    if (alreadyThere) {
      console.log('WizNavigationManager.Err : dynamic route : ' + child.path + 'already exist');
      return; // rien à faire dans ce cas
    }

    // on duplique l'arbre de routes
    const tmpConfig: Routes = this.router.config;
    // on trouve le père de l'enfant (pas d'ajout à la racine)
    const daddy: IWizRoute = this.getRecursePathRoute(parentPath, tmpConfig);
    if (!daddy.children) { daddy.children = []; }
    // console.log('daddy ' + daddy.path + ' ' + daddy.fullPath);
    child.fullPath = daddy.fullPath + '/' + child.path; // alimentation du fullPath pour navig
    daddy.children.push(child);
    this.router.resetConfig(tmpConfig);
  }

  /**
   * suppression d'une route (et de tous ses enfants éventuels)
   * @param path : le path de la route à détruire
   */
  public static removeRoute(path: string): void {
    // on duplique l'arbre de routes
    const tmpConfig: Routes = this.router.config;
    // on trouve le parent
    const daddy: IWizRoute = this.getPathParentRoute(path, tmpConfig);
    if (daddy) {
      daddy.children.forEach((child: IWizRoute) => {
        if (child.path === path) { // trouvé l'enfant
          // console.log('removeRoute', daddy.path, child.path);
          daddy.children.splice(daddy.children.indexOf(child), 1);
        }
      });
     }
    this.router.resetConfig(tmpConfig);
  }

}
